import { useMemo, useState } from "react";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { GridActionsCellItem, type GridColDef } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { api } from "../lib/api";
import { describeCheckError } from "../lib/check-error";
import { useAsync } from "../lib/hooks";
import { formatMs, newestFirst } from "../lib/format";
import { useI18n } from "../lib/i18n";
import type { LinkReport, LinkReportDetail } from "../lib/types";
import { Badge, Button, Card, ErrorBox, Loading, Modal } from "../components/ui";
import { DataTable } from "../components/DataTable";
import { HealthBar } from "./Dashboard";

export function Reports() {
  const { t, fmtDate } = useI18n();
  const { data, loading, error, reload } = useAsync(() => api.reports.list(100), []);
  const [detail, setDetail] = useState<LinkReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const reports = data ?? [];

  async function generate() {
    setBusy(true);
    setActionError(null);
    try {
      await api.reports.generate();
      await reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const columns = useMemo<GridColDef<LinkReport>[]>(
    () => [
      {
        field: "generatedAt",
        headerName: t("table.time"),
        flex: 1.1,
        minWidth: 150,
        valueGetter: (v: string) => fmtDate(v),
      },
      {
        field: "triggeredBy",
        headerName: t("table.source"),
        flex: 0.6,
        minWidth: 100,
        renderCell: ({ row }) => (
          <Chip size="small" variant="outlined" label={t(`trigger.${row.triggeredBy}`)} />
        ),
      },
      {
        field: "totalLinks",
        headerName: t("reports.total"),
        type: "number",
        flex: 0.4,
        minWidth: 80,
      },
      {
        field: "healthy",
        headerName: t("status.healthy"),
        type: "number",
        flex: 0.4,
        minWidth: 90,
      },
      {
        field: "unhealthy",
        headerName: t("status.unhealthy"),
        type: "number",
        flex: 0.4,
        minWidth: 90,
      },
      {
        field: "unknown",
        headerName: t("status.unknown"),
        type: "number",
        flex: 0.4,
        minWidth: 90,
      },
      {
        field: "ratio",
        headerName: t("reports.ratio"),
        flex: 0.9,
        minWidth: 120,
        sortable: false,
        renderCell: ({ row }) => (
          <HealthBar healthy={row.healthy} unhealthy={row.unhealthy} unknown={row.unknown} />
        ),
      },
      {
        field: "durationMs",
        headerName: t("reports.duration"),
        type: "number",
        flex: 0.6,
        minWidth: 100,
        valueFormatter: (v: number) => formatMs(v),
      },
      {
        field: "actions",
        type: "actions",
        headerName: "",
        width: 56,
        getActions: ({ row }) => [
          <GridActionsCellItem
            key="detail"
            icon={<VisibilityIcon fontSize="small" />}
            label={t("common.details")}
            onClick={() => setDetail(row)}
            showInMenu
          />,
        ],
      },
    ],
    [t, fmtDate],
  );

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>{t("reports.title")}</h1>
          <p className="muted">{t("reports.subtitle")}</p>
        </div>
        <Button variant="primary" loading={busy} onClick={generate}>
          {t("reports.generate")}
        </Button>
      </header>

      {error && <ErrorBox message={error} />}
      {actionError && <ErrorBox message={actionError} />}

      <Card>
        {loading ? (
          <Loading />
        ) : (
          <DataTable
            rows={newestFirst(reports, "generatedAt")}
            columns={columns}
            showToolbar
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          />
        )}
      </Card>

      {detail && <ReportDetail report={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function ReportDetail({ report, onClose }: { report: LinkReport; onClose: () => void }) {
  const { t, fmtDate } = useI18n();
  const [onlyFailed, setOnlyFailed] = useState(false);
  const details = onlyFailed
    ? report.details.filter(d => d.healthStatus !== "healthy")
    : report.details;

  const columns = useMemo<GridColDef<LinkReportDetail>[]>(
    () => [
      { field: "brand", headerName: t("table.brand"), flex: 0.7, minWidth: 100 },
      { field: "domain", headerName: t("table.domain"), flex: 1.6, minWidth: 170 },
      {
        field: "healthStatus",
        headerName: t("table.status"),
        flex: 0.6,
        minWidth: 105,
        renderCell: ({ row }) => <Badge status={row.healthStatus} />,
      },
      {
        field: "statusCode",
        headerName: t("table.http"),
        type: "number",
        flex: 0.4,
        minWidth: 80,
        valueFormatter: (v: number | null) => v ?? "—",
      },
      {
        field: "responseMs",
        headerName: t("table.response"),
        type: "number",
        flex: 0.5,
        minWidth: 95,
        valueFormatter: (v: number | null) => formatMs(v),
      },
      {
        field: "error",
        headerName: t("table.error"),
        flex: 1,
        minWidth: 110,
        valueGetter: (_v, row) =>
          describeCheckError(
            t,
            { code: row.errorCode, statusCode: row.statusCode, detail: row.errorDetail },
            row.error,
          ) ?? "—",
      },
      {
        field: "checkerIp",
        headerName: t("table.checkerIp"),
        flex: 0.7,
        minWidth: 120,
        valueGetter: (v: string | null) => v ?? "—",
      },
    ],
    [t],
  );

  const stats = [
    { label: t("dash.totalLinks"), value: report.totalLinks, color: undefined },
    { label: t("status.healthy"), value: report.healthy, color: "success.main" },
    { label: t("status.unhealthy"), value: report.unhealthy, color: "error.main" },
    { label: t("status.unknown"), value: report.unknown, color: "warning.main" },
  ];

  return (
    <Modal
      title={t("reports.detailTitle", { id: report.id, date: fmtDate(report.generatedAt) })}
      onClose={onClose}
    >
      <Stack direction="row" spacing={3} useFlexGap sx={{ flexWrap: "wrap" }}>
        {stats.map(s => (
          <Stack key={s.label} sx={{ minWidth: 84 }}>
            <Typography variant="h6" sx={{ color: s.color, fontWeight: 700, lineHeight: 1.2 }}>
              {s.value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {s.label}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={onlyFailed}
            onChange={e => setOnlyFailed(e.target.checked)}
          />
        }
        label={<Typography variant="body2">{t("reports.onlyFailed")}</Typography>}
      />

      <DataTable
        rows={newestFirst(details, "checkedAt")}
        columns={columns}
        height={360}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
      />
    </Modal>
  );
}
